import React, { useMemo } from 'react';
import { MinimalLazyVideo } from './MinimalLazyVideo';
import { useMediaOptimization, extractPublicId } from '@/utils/media';
import { getOptimalVideoUrl } from '@/utils/videoCompatibility';

interface LazyVideoProps {
  src: string;
  className?: string;
  alt?: string;
  width?: number;
  height?: number;
  controls?: boolean;
  muted?: boolean;
  autoPlay?: boolean;
  loop?: boolean;
  onPlay?: () => void;
  onError?: () => void;
  postId?: string; // For stable keys
}

/**
 * Enhanced LazyVideo with media optimization
 * Falls back to MinimalLazyVideo for simplicity when needed
 */
export const LazyVideo = ({
  src,
  className = '',
  alt = 'Play video',
  width = 480,
  height = 270,
  controls = true,
  muted = true,
  autoPlay = false,
  loop = false,
  onPlay,
  onError,
  postId
}: LazyVideoProps) => {
  const { mode, poster: buildPoster, video: buildVideo } = useMediaOptimization();
  
  // Memoize video configuration to prevent re-renders when connection mode stays same
  const videoConfig = useMemo(() => {
    const actualWidth = mode === 'lite' ? 320 : width;
    const actualHeight = mode === 'lite' ? Math.round(320 * (height / width)) : height;
    const publicId = extractPublicId(src);
    const posterUrl = buildPoster(publicId, actualWidth, actualHeight);
    const baseVideoUrl = buildVideo(publicId);
    const videoUrl = getOptimalVideoUrl(baseVideoUrl);
    
    return { actualWidth, actualHeight, posterUrl, videoUrl };
  }, [src, mode, width, height, buildPoster, buildVideo]);

  // For lite mode or when we want maximum compatibility, use the minimal version
  if (mode === 'lite') {
    return (
      <div key={postId ? `video-${postId}-${mode}` : `${src}-${mode}`}>
        <MinimalLazyVideo
          src={videoConfig.videoUrl}
          poster={videoConfig.posterUrl}
          className={className}
          postId={postId}
          style={{
            width: `${videoConfig.actualWidth}px`,
            height: `${videoConfig.actualHeight}px`
          }}
        />
      </div>
    );
  }

  // Fallback to minimal implementation for better compatibility
  return (
    <div key={postId ? `video-${postId}-${mode}` : `${src}-${mode}`}>
      <MinimalLazyVideo
        src={videoConfig.videoUrl}
        poster={videoConfig.posterUrl}
        className={className}
        postId={postId}
        style={{
          width: `${videoConfig.actualWidth}px`,
          maxWidth: '100%',
          height: 'auto'
        }}
      />
    </div>
  );
};